import '../styles/styles.scss';
import { BrowserModule } from '@angular/platform-browser';
import {
    NgModule,
    ApplicationRef
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    RouterModule,
    PreloadAllModules,
    RouteReuseStrategy
} from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    removeNgStyles,
    createNewHosts,
    createInputTransfer
} from '@angularclass/hmr';
import { CookieModule } from 'ngx-cookie';

import { ROUTES } from "./app.routes";
import { AppComponent } from './app.component';
import { ImageUploadService } from "./services/Common/imageupload.service";

// App views
import { DashboardsModule } from "./views/dashboards/dashboards.module";
import { AppviewsModule } from "./views/appviews.module";
import { ErrorsModule } from "./views/errors/errors.module";

import { AuthenticationService, LoginGuard, Utilities } from "./services"

import { ProgrammeService } from "./services/DataServices/programme.service";

// App modules/components
import { LayoutsModule } from "./components/common/layouts/layouts.module";

import { CustomReuseStrategy } from "./helpers/sticky.routereusestrategy"

/*
 * Platform and Environment providers/directives/pipes
 */
import { ENV_PROVIDERS } from './environment';

import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { AppState, InternalStateType } from './app.service';

import 'metisMenu';
import 'bootstrap';
import 'jquery-sparkline';
import { PermissionService } from "./services/SecurityServices/permission.service";
import { ViewCustomersGuard } from "./services/SecurityServices/view-customers.guard";
import { ViewCustomerGuard } from "./services/SecurityServices/view-customer.guard";
import { ViewUserGuard } from "./services/SecurityServices/view-user.guard";
import { ViewUsersGuard } from "./services/SecurityServices/view-users.guard";
import { ViewSitesGuard } from "./services/SecurityServices/view-sites.guard";
import { ViewSiteGuard } from "./services/SecurityServices/view-site.guard";
import { ViewLoyaltyTransactionLogGuard } from "./services/SecurityServices/view-loyalty-transaction-log.guard";
import { ViewLoyaltyTransactionLogsGuard } from "./services/SecurityServices/view-loyalty-transaction-logs.guard";
import { ViewFraudReportGuard } from "./services/SecurityServices/view-fraud-report.guard";
import { ViewFraudReportsGuard } from "./services/SecurityServices/view-fraud-reports.guard";
import { ViewCustomerMergeGuard } from "./services/SecurityServices/view-customer-merge.guard";
import { ViewAuditReportGuard } from "./services/SecurityServices/view-audit-report.guard";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { APIInterceptor } from "./helpers/http.APIInterceptor";

import {ModalModule} from 'angular2-modal';
import {BootstrapModalModule} from 'angular2-modal/plugins/bootstrap';
import { AngularMultiSelectModule } from 'angular2-multiselect-dropdown/angular2-multiselect-dropdown';
import { AgmCoreModule } from "@agm/core";
// Application wide providers
const APP_PROVIDERS = [
    ...APP_RESOLVER_PROVIDERS,
    AppState
];

type StoreType = {
    state: InternalStateType,
    restoreInputValues: () => void,
    disposeOldHosts: () => void
};

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  /**
   * Import Angular's modules.
   */
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    DashboardsModule,
    ErrorsModule,
    LayoutsModule,
    AppviewsModule,
    CookieModule.forRoot(),
    RouterModule.forRoot(ROUTES, {
      useHash: true,
      preloadingStrategy: PreloadAllModules,
    }),
    ModalModule.forRoot(),
    BootstrapModalModule,
    BrowserAnimationsModule,
    AngularMultiSelectModule,
    AgmCoreModule.forRoot({
      apiKey: "AIzaSyBCP-bMjmH6cM7vuPLicYemZDPVusLj1mI",
      libraries: ["geometry"],
    }),
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    ENV_PROVIDERS,
    APP_PROVIDERS,
    LoginGuard,
    ViewCustomerMergeGuard,
    ViewCustomersGuard,
    ViewCustomerGuard,
    ViewUserGuard,
    ViewUsersGuard,
    ViewSitesGuard,
    ViewSiteGuard,
    ViewLoyaltyTransactionLogGuard,
    ViewLoyaltyTransactionLogsGuard,
    ViewFraudReportGuard,
    ViewFraudReportsGuard,
    ViewAuditReportGuard,
    PermissionService,
    AuthenticationService,
    ProgrammeService,
    ImageUploadService,
    Utilities,
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: APIInterceptor,
      multi: true,
    },
  ],
  exports: [
   // AgmCoreModule
  ]
})
export class AppModule {
  constructor(public appRef: ApplicationRef, public appState: AppState) {}

  public hmrOnInit(store: StoreType) {
    if (!store || !store.state) {
      return;
    }
    console.log("HMR store", JSON.stringify(store, null, 2));
    /**
     * Set state
     */
    this.appState._state = store.state;
    /**
     * Set input values
     */
    if ("restoreInputValues" in store) {
      let restoreInputValues = store.restoreInputValues;
      setTimeout(restoreInputValues);
    }

    this.appRef.tick();
    delete store.state;
    delete store.restoreInputValues;
  }

  public hmrOnDestroy(store: StoreType) {
    const cmpLocation = this.appRef.components.map(
      (cmp) => cmp.location.nativeElement
    );
    /**
     * Save state
     */
    const state = this.appState._state;
    store.state = state;
    /**
     * Recreate root elements
     */
    store.disposeOldHosts = createNewHosts(cmpLocation);
    /**
     * Save input values
     */
    store.restoreInputValues = createInputTransfer();
    /**
     * Remove styles
     */
    removeNgStyles();
  }

  public hmrAfterDestroy(store: StoreType) {
    /**
     * Display new elements
     */
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }
}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./src/app/app.module.ts